import {SessionItem, WordReview} from "../types/sessionTypes";
import {useEffect, useMemo, useRef, useState} from "react";
import {useAudio} from "../../../common/context/AudioContext";
import analyzeReview, {ReviewAnalysisResult} from "../utils/ReviewAnalysis";
import {useSnackbar} from "../../../common/context/SnackbarContext";
import {getAudioForSource} from "../../../common/utils/audioUtils";

export interface StudySessionProps {
    items: SessionItem[]
    words: WordReview[]
    onItemReviewed: (item: SessionItem, fails: number) => void
}

export interface WordReviewWithSound extends WordReview {
    sound: HTMLAudioElement | null
}

export interface SessionElements {
    currentTaskType: string | null
    currentWord: WordReviewWithSound | null
    userInput: string
    onUserInputChanged: (input: string) => void
    answerSubmitted: boolean
    isCorrectAnswer: boolean
    submittedAnswerHasTypo: boolean
    submittedAnswerInDontRejectWords: boolean
    revealCorrectAnswer: boolean
    submitAnswer: () => void
    toggleRevealAnswer: () => void
    handleIntentionalFail: () => void
    nextItem: () => void
    undo: () => void
    reshuffle: () => void
    allItemsFinished: boolean
    playItemSound: () => void
}

function useStudySession({items, words, onItemReviewed}: StudySessionProps) : SessionElements {
    
    const wordsWithSound = useMemo(() => 
        words.map(word => ({...word, sound: getAudioForSource(word.resources?.lemma)}))
        ,[words]);

    const wordMap = useMemo(() => {
        const map: {[key: string]: WordReviewWithSound} = {};
        wordsWithSound.forEach(word => {
            map[word.wordId] = word;
        })
        return map;
    }, [wordsWithSound]);
    
    const [remainingItems, setRemainingItems] = useState<SessionItem[]>(
        () => items.filter(item => item.status === 'ready' || item.status === 'pending')
    );

    useEffect(() => {
        setRemainingItems(items.filter(item => item.status === 'ready' || item.status === 'pending'));
    }, [items]);

    const currentItem = useMemo(() => remainingItems.length > 0 ? remainingItems[0] : null, [remainingItems]);
    
    const currentTaskType = useMemo(() => currentItem ? currentItem.type : null, [currentItem]);
    
    const currentWord = useMemo(() => {
        if (currentTaskType !== 'translation' && currentTaskType !== 'recollection') 
            return null;
        return wordMap[currentItem!.content.wordId];
    }, [currentItem, currentTaskType, wordMap]);

    const [providedAnswer, setProvidedAnswer] = useState<string>('');
    const [answerSubmitted, setAnswerSubmitted] = useState<boolean>(false);
    const [isCorrectAnswer, setIsCorrectAnswer] = useState<boolean>(false);
    const [submittedAnswerHasTypo, setSubmittedAnswerHasTypo] = useState<boolean>(false);
    const [submittedAnswerInDontRejectWords, setSubmittedAnswerInDontRejectWords] = useState<boolean>(false);
    const [revealCorrectAnswer, setRevealCorrectAnswer] = useState<boolean>(false);
    const [wrongAnswerTime, setWrongAnswerTime] = useState<number | null>(null);
    const [emptyAnswerTime, setEmptyAnswerTime] = useState<number | null>(null);
    const [allItemsFinished, setAllItemsFinished] = useState<boolean>(false);
    
    const failureMap = useRef<Map<number, number>>(new Map());
    
    const {play, pause} = useAudio();

    const {showSnackbar, hideSnackbar} = useSnackbar();

    const playItemSound = () => {
        pause();
        
        if (currentTaskType === 'translation' && currentWord?.sound) {
            play(currentWord.sound);
            return;
        }
        
        if (currentTaskType === 'recollection' && revealCorrectAnswer && currentWord?.sound) {
            play(currentWord.sound);
            return;
        }
    };

    useEffect(() => playItemSound(), [currentItem, revealCorrectAnswer]);

    const handleInputChange = (input: string) => {
        if (!answerSubmitted) {
            setProvidedAnswer(input);
            setSubmittedAnswerInDontRejectWords(false);
        }
    }

    const toggleRevealAnswer = () => {
        setRevealCorrectAnswer(prev => !prev);
    }

    const submitAnswer = async () => {
        if (!providedAnswer.length) {
            if (emptyAnswerTime && (new Date().getTime() - emptyAnswerTime) < 500)
                handleIntentionalFail();
            else
                setEmptyAnswerTime(new Date().getTime());
            return;
        }

        if (currentTaskType === 'translation' || currentTaskType === 'recollection') {
            const analysis : Promise<ReviewAnalysisResult> = currentTaskType === 'translation' ?
                analyzeReview(providedAnswer, currentWord!.translations, currentWord!.homonymTranslations, {ratio: 3}) :
                analyzeReview(providedAnswer, currentWord!.lemma, currentWord!.synonyms.map(syn => syn.lemma), "None");
            const {isCorrect, hasTypo, isDifferentWord} = await analysis;
            if (isCorrect) {
                setAnswerSubmitted(true);
                setIsCorrectAnswer(true);
                setSubmittedAnswerHasTypo(hasTypo!);
                if (hasTypo) {
                    showSnackbar("Answer accepted, but check your typo.", "info");
                }
            }
            else {
                if (isDifferentWord) {
                    setSubmittedAnswerInDontRejectWords(isDifferentWord);
                    showSnackbar("Technically correct, but looking for something else", "warning");
                }
                else {
                    setAnswerSubmitted(true);
                    setIsCorrectAnswer(false);
                    setWrongAnswerTime(new Date().getTime());
                }
            }
        }
    }

    const handleIntentionalFail = () => {
        setAnswerSubmitted(true);
        setIsCorrectAnswer(false);
        setRevealCorrectAnswer(true);
    };

    const nextItem = () => {
        if (!isCorrectAnswer && wrongAnswerTime && (new Date().getTime() - wrongAnswerTime) < 500)
            return;

        if (isCorrectAnswer) {
            onItemReviewed(currentItem!, failureMap.current.get(currentItem!.seq) ?? 0);
            setRemainingItems(prev => prev.slice(1));
        } else {
            if (failureMap.current.has(currentItem!.seq))
                failureMap.current.set(currentItem!.seq, failureMap.current.get(currentItem!.seq)! + 1);
            else
                failureMap.current.set(currentItem!.seq, 1);
            postponeCurrentItem();
        }

        clearAnswer();
    };

    const postponeCurrentItem = () => {
        if (remainingItems.length <= 1) {
            return;
        }

        let insertBefore = remainingItems.length;
        if (currentItem?.prequelTo) {
            const prequelIndex = remainingItems.findIndex(item => item.seq === currentItem.prequelTo);
            if (prequelIndex >= 2)
                insertBefore = Math.floor(prequelIndex / 2);
            else if (prequelIndex !== -1)
                insertBefore = prequelIndex;
        }

        const newIndex = Math.floor(Math.random() * insertBefore);
        setRemainingItems(prev => {
            const newItems = prev.slice(1);
            newItems.splice(newIndex, 0, prev[0]);
            return newItems;
        });
    };

    const clearAnswer = () => {
        setAnswerSubmitted(false);
        setIsCorrectAnswer(false);
        setProvidedAnswer('')
        setSubmittedAnswerHasTypo(false);
        setSubmittedAnswerInDontRejectWords(false);
        setRevealCorrectAnswer(false);
        hideSnackbar();
    };

    const cleanAudioFiles = () => {
        wordsWithSound.forEach(w => w.sound = null);
    }

    useEffect(() => {
        setAllItemsFinished(remainingItems.length === 0);
        if (remainingItems.length === 0) {
            cleanAudioFiles();
        }
    }, [remainingItems]);

    return {
        currentTaskType,
        currentWord,
        userInput: providedAnswer,
        onUserInputChanged: handleInputChange,
        answerSubmitted,
        isCorrectAnswer,
        submittedAnswerHasTypo,
        submittedAnswerInDontRejectWords,
        revealCorrectAnswer,
        submitAnswer,
        toggleRevealAnswer,
        handleIntentionalFail,
        nextItem,
        undo: clearAnswer,
        reshuffle: postponeCurrentItem,
        allItemsFinished,
        playItemSound
    }
    
}

export default useStudySession;